

































































































import Vue from 'vue';
import ParseUrlParams from '@/components/url/ParseUrlParams.vue';
import Table from '@/components/table/newTable/Table.vue';
import FieldDatePicker from '@/components/form/FieldDatePicker.vue';
import TableFilter from '@/components/table/newTable/TableFilter.vue';
import Button from '@/components/form/Button.vue';
import TableFilterButtonsPanel from '@/components/table/newTable/TableFilterButtonsPanel.vue';
import TableToolButtonsPanel from '@/components/table/newTable/TableToolButtonsPanel.vue';
import TableFilterValues from '@/components/table/newTable/TableFilterValues.vue';
import TablePagination from '@/components/table/newTable/TablePagination.vue';
import Preloader from '@/components/preloader/Preloader.vue';
import { mapActions, mapGetters } from 'vuex';
import {
  columns,
  dimensions,
  metrics,
  filters,
} from '@/configs/reportBuilderConfig';
import { getTranslate } from '@/helpers/translateHelpers';
import ReportBuilderService from '@/services/ReportBuilderService';
import { getIcon, TIconSvg } from '@/configs/iconsConfigs';
import {
  formatDate,
  sortArrayToDropDownListByParams,
} from '@/helpers/dataHelpers';
import TableService from '@/services/TableService';
import {
  PaginationData,
  TDropDownList,
  TUrlParams,
  userRoles,
} from '@/types';
import {
  EFilterValuePrefixes,
  TTableColumns,
  TSelectedFilterValue,
  TSelectedFilterValues,
  ETableUrlFilters,
} from '@/components/table/types';
import {
  tableFieldConvert,
  defaultPerPage,
} from '@/configs/tableConfigs';
import { userCanByRole } from '@/helpers/authHelpers';
import { TReportBuilderData } from '@/helpers/reportBuilderHelpers';
import { splitUrlParamsValue } from '@/helpers/urlParamsHelpers';

export default Vue.extend({
  name: 'ReportBuilder',
  components: {
    ParseUrlParams,
    Table,
    FieldDatePicker,
    TableFilter,
    Button,
    TableFilterButtonsPanel,
    TableToolButtonsPanel,
    TableFilterValues,
    TablePagination,
    Preloader,
  },

  mounted() {
    this.$store.commit('changeTitle', this.$t('pages.reportBuilder.title'));

    if (!this.dateValues.length) {
      const dateNow = formatDate(new Date());
      this.dateValues = [dateNow, dateNow];
    }

    this.$store.dispatch('campaignGroups/loadGroups', {
      columns: ['id', 'name'],
    });
    this.$store.dispatch('countries', {
      is_available: 0,
      per__page: 0,
      sort: 'name',
    });

    if (userCanByRole(this.roles.ADMIN)) {
      this.$store.dispatch('users');
    }
  },

  watch: {
    dateValues(newValue: Array<string>) {
      TableService.setFilterDateValuesToUrl(newValue);
    },
    paginationData(newValue: PaginationData) {
      this.perPageValue = Number(newValue.perPage);
    },
    groupItems: {
      handler(newValue: TDropDownList) {
        this.filters = filters.setItems(newValue, tableFieldConvert('group'));
      },
      immediate: true,
    },
    geoItems: {
      handler(newValue: TDropDownList) {
        this.filters = filters.setItems(newValue, tableFieldConvert('geo'));
      },
      immediate: true,
    },
    userItems: {
      handler(newValue: TDropDownList) {
        this.filters = filters.setItems(
          newValue,
          tableFieldConvert('campaignUserId')
        );
      },
      immediate: true,
    },
  },

  computed: {
    ...mapGetters({
      data: 'reportBuilder/getReportBuilderRows',
      paginationData: 'reportBuilder/getReportBuilderPaginationData',
    }),
    refs(): any {
      return this.$refs;
    },
    metricsIsExist(): boolean {
      return this.metricValues.length > 0;
    },
    groupItems(): TDropDownList {
      return sortArrayToDropDownListByParams(
        this.$store.getters['campaignGroups/getGroups'],
        'name',
        'id'
      );
    },
    geoItems(): TDropDownList {
      return this.$store.getters['countriesList'];
    },
    userItems(): TDropDownList {
      if (userCanByRole(this.roles.ADMIN)) {
        return this.$store.getters['userList'];
      } else {
        return [];
      }
    },
    roles(): typeof userRoles {
      return userRoles;
    },
    selectedFilterValues(): TSelectedFilterValues {
      return [
        ...this.filterValues,
        ...this.dimensionValues,
        ...this.metricValues,
      ];
    },
  },

  methods: {
    ...mapActions({
      loadReportBuilderAction: 'reportBuilder/loadReportBuilder',
    }),
    getTranslate(path: string): string {
      return getTranslate(path);
    },
    getIcon(iconName: string): TIconSvg {
      return getIcon(iconName);
    },
    changeSort(sort: string) {
      this.sortValue = sort;
      this.uploadData();
    },
    changePerPage(perPage: number) {
      this.perPageValue = perPage;
      this.uploadData();
    },
    changePage(page: number) {
      this.pageValue = page;
      this.uploadData();
    },
    changeFilters(filterValues: TSelectedFilterValues) {
      this.filterValues = filterValues;
      TableService.setFilterValuesToUrl(filterValues, ETableUrlFilters.filter);
    },
    changeDimensions(dimensionValues: TSelectedFilterValues) {
      this.columns = ReportBuilderService.changeDimensionMetric(
        this.columns,
        this.dimensions,
        dimensionValues
      );

      this.dimensionValues = dimensionValues;
      TableService.setFilterValuesToUrl(
        dimensionValues,
        ETableUrlFilters.dimensions
      );
    },
    changeMetrics(metricValues: TSelectedFilterValues) {
      this.columns = ReportBuilderService.changeActiveMetrics(
        this.columns,
        metricValues,
        this.dimensionValues
      );

      this.metricValues = metricValues;
      TableService.setFilterValuesToUrl(metricValues, ETableUrlFilters.metrics);
    },
    removeFiltersValue(value: TSelectedFilterValue) {
      switch (value.prefix) {
        case EFilterValuePrefixes.filter:
          this.refs.filters.removeSelectedValue(value);
          break;
        case EFilterValuePrefixes.dimension:
          this.refs.dimensions.removeSelectedValue(value);
          break;
        case EFilterValuePrefixes.metric:
          this.refs.metrics.removeSelectedValue(value);
          break;
      }
    },
    parseFromUrlParams(urlParams: TUrlParams) {
      for (const param of urlParams) {
        switch (param.name) {
          case ETableUrlFilters.date:
            this.dateValues = splitUrlParamsValue(param.value);
            break;
          case ETableUrlFilters.sort:
            this.sortValue = param.value;
            break;
          case ETableUrlFilters.per__page:
            this.perPageValue = parseInt(param.value);
            break;
          case ETableUrlFilters.page:
            this.pageValue = parseInt(param.value);
            break;
          case ETableUrlFilters.filter:
            this.filters = filters.setFilterValues(param);
            break;
          case ETableUrlFilters.dimensions:
            this.dimensions = dimensions.setFilterValues(param);
            break;
          case ETableUrlFilters.metrics:
            this.metrics = metrics.setFilterValues(param);
            break;
        }
      }

      this.$nextTick(() => {
        if (this.metricValues.length) {
          this.uploadData();
        }
      });
    },
    async uploadData() {
      if (this.loadData) return;

      this.loadData = true;

      const data: TReportBuilderData = {
        sort: this.sortValue,
        perPage: this.perPageValue,
        page: this.pageValue,
        date: this.dateValues,
        filters: this.filterValues,
        dimensions: this.dimensionValues,
        metrics: this.metricValues,
      };

      await this.loadReportBuilderAction(data);

      this.loadData = false;
    },
  },

  data() {
    return {
      columns: columns.formating().getColumns() as TTableColumns,
      filters: filters.formating().getColumns() as TTableColumns,
      dimensions: dimensions.formating().getColumns() as TTableColumns,
      metrics: metrics.formating().getColumns() as TTableColumns,

      sortValue: '',
      perPageValue: defaultPerPage(),
      pageValue: 1,
      dateValues: [] as Array<string>,
      filterValues: [] as TSelectedFilterValues,
      dimensionValues: [] as TSelectedFilterValues,
      metricValues: [] as TSelectedFilterValues,

      loadData: false,
    };
  },
});
